<script lang="ts">
import type { CreateElement, VNode, VNodeChildren, PropType } from "vue";
import { defineComponent } from "vue";
import type {
  BmGridColumnConfiguration,
  BmGridExportEvent,
  ColumnAlignment,
  ContentRow,
  Row,
  RowGroup,
} from "@/components/controls/grid/BmGridData";
import BmGridData, {
  isContentRow,
  isRowGroup,
  SummaryPlacement,
} from "@/components/controls/grid/BmGridData";
import BmBtnExpand from "@/components/controls/input/BmBtnExpand.vue";
import { BrewManError } from "@/app/errors";
import type { DnDEvent } from "vue-easy-dnd";
import { Drag, Drop } from "vue-easy-dnd";
import type { Location } from "vue-router";
import {
  encodeStringToUtf8WithBom,
  initiateDownloadFromBytes,
  isNotNullOrUndefined,
} from "@/app/apiutils";
import {
  GridGroupValue,
  GridRowValue,
  documentGenerationService,
} from "@/app/services/generated/documentgenerationService";
import api from "@/app/api";
import { EnumDocumentFileType } from "@/app/services/generated/enums/enumDocumentFileType.generated";
import type { AgGridInterface } from "@/components/controls/grid/AgGrid";
import AgGrid from "@/components/controls/grid/AgGrid";
import type { RowNode } from "@ag-grid-community/core";
import { isRunningEndToEndTests } from "@/app/ui/utilities";
import { indexedKeyComparer } from "@/components/controls/grid/BmGridComparers";
import messages, { translateWithParams } from "@/plugins/i18n";
import { useRoot } from "@/components/composables/root";
import { googleSheetLinkService } from "@/app/services/generated/googlesheetlinkService";
import userStore from "@/store/modules/userStore";
import Guid from "@/app/types/common/guid";
import { mainMenu } from "@/app/ui/navigation/mainMenu";
import router from "@/router/router";

export interface GridDragDropEvent {
  /** Row key that was dragged */
  draggedRowKeys: string[];
  /** The row key that the item should be placed under. If undefined, the item should be placed at the top of the list.*/
  droppedOnRowKey: string | undefined;
}

/**
 * Brewman Grid component.
 *
 * See BmGridData for the properties required to set up the data part of the grid.
 *
 * Dragging only works on desktop view
 */
export default defineComponent({
  name: "BmGrid",
  mixins: [BmGridData],
  components: { Drag, Drop },
  props: {
    allowExpand: {
      type: Boolean,
      default: false,
    },
    allowSelect: {
      type: Boolean,
      default: false,
    },
    allowDrag: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether the draggable rows can be dropped back inside this grid
     */
    allowDrop: {
      type: Boolean,
      default: false,
    },
    rowDraggable: {
      type: Function as PropType<(item: any) => boolean>,
      default: (item: any) => true,
    },
    rowExpandable: {
      type: Function as PropType<(item: any) => boolean>,
      default: (item: any) => false,
    },
    subGrid: {
      type: Boolean,
      default: false,
    },
    cardColumns: {
      type: Number,
      default: 2,
    },
    height: {
      type: [Number, String],
      default: null,
    },
    overrideHeight: {
      type: [Number, String],
      default: null,
    },
    allowCardView: {
      type: Boolean,
      default: true,
    },
    /**
     * Provide a function that returns an array of classes to add to the row element of the grid.
     * Note. This only supports content rows (not grouping rows).
     */
    rowClasses: {
      type: Function as PropType<(row: any) => string[]>,
      default: (row: any) => [],
    },
    /**
     * Provide a function that returns a Location;
     * This will be used to wrap the td elements in router-links for redirects
     */
    rowClickRoute: {
      type: Function as PropType<((item: any) => Location | null) | null>,
      default: null,
    },
    /**
     * Restricts the number of visible rows the grid component will initially display.
     * Note. this is not a filter and does not effect filter counts.
     *
     * The user can remove this restriction, but cannot re-apply it.
     */
    initialVisibleRows: {
      type: Number,
      default: null,
    },
    disableAgGrid: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableCardView: {
      type: Boolean,
      required: false,
      default: false,
    },
    isFixedShapeReport: {
      type: Boolean,
      required: false,
      default: false,
    },
    includeIds: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      showAllRows: false,
      currentDraggedRow: null as Row | null,
      /**
       * On Firefox, a parent anchor tag component is clicked even if a child
       * input element prevents event propagation. This means the row will always
       * be clicked if it has a route.
       *
       * For now, disable row click on the first table cell if using firefox.
       *
       * Progress: https://github.com/vuejs/vue/issues/12490
       */
      shouldAllowRowClickRouteOnSelectableCell: true as boolean,
      pdfTitle: "" as string,
      pdfColumns: [] as string[],
      pdfGrid: [] as GridGroupValue[],
    };
  },
  computed: {
    visibleRowLimit(): number | null {
      // Truncate rows if the user has not asked for all rows, and there are no groups or footer rows.
      if (
        !this.showAllRows &&
        Object.keys(this.computedGroupedColumns).length === 0 &&
        !Object.values(this.visibleColumns).some((col) => col.footer)
      ) {
        return this.initialVisibleRows;
      }

      return null;
    },
    isAgGridActive(): any {
      // @TODO: Implement AG Grid in e2e tests when dom unmounting works:
      //  https://github.com/cypress-io/cypress/issues/7306
      return (
        !this.disableAgGrid &&
        !this.isCardView &&
        !this.allowDrag &&
        !this.allowDrop &&
        !isRunningEndToEndTests() // Use BmGrid for all E2E tests
      );
    },
    isCardView(): boolean {
      // We revert to desktop view if the number of groupings exceed 2 as card view becomes hard to read at that stage
      return (
        !this.disableCardView &&
        this.$vuetify.breakpoint.xsOnly &&
        Object.keys(this.computedGroupedColumns).length < 3 &&
        this.allowCardView
      );
    },
    draggableItems(): readonly string[] {
      if (this.currentDraggedRow?.isSelected) {
        return this.selectedItemKeys;
      } else {
        return this.currentDraggedRow ? [this.currentDraggedRow.rowKey] : [];
      }
    },
    isRowClickable(): any {
      return (
        // eslint-disable-next-line vue/no-deprecated-dollar-listeners-api
        (this.$listeners && this.$listeners["row-click"]) ||
        !!this.rowClickRoute
      );
    },
    gridHeightWithUnits() {
      if (!this.height) {
        return undefined;
      }

      if (typeof this.height === "string") {
        return this.height;
      }

      if (typeof this.height === "string") {
        return this.height;
      }

      return isNaN(this.height) ? this.height : `${this.height}px`;
    },
  },
  watch: {
    isAgGridActive: {
      // @ts-ignore
      handler: "activeGridWatcher",
      immediate: true,
    },
  },
  mounted() {
    useRoot().$on("bm-grid-export", this.handleExport);
  },
  created() {
    const isFirefox = navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
    this.shouldAllowRowClickRouteOnSelectableCell = !(
      isFirefox && this.allowSelect
    );
  },
  async beforeDestroy() {
    useRoot().$off("bm-grid-export", this.handleExport);
  },
  methods: {
    activeGridWatcher(isAgGridActive: boolean) {
      this.setDisableReactiveRows(isAgGridActive);
    },
    async handleExport(event: BmGridExportEvent) {
      if (event.stateId && event.stateId == this.gridState.stateId) {
        // Instruction is for us
        if (event.format == "csv") {
          initiateDownloadFromBytes(
            event.filename,
            "text/csv;charset=utf-8",
            () =>
              Promise.resolve(
                encodeStringToUtf8WithBom(this.renderCsv(",", '"'))
              )
          );
        } else if (event.format == "pdf") {
          initiateDownloadFromBytes(event.filename, "application/pdf", () =>
            Promise.resolve(this.renderPdf(event.fileTitle))
          );
        } else if (event.format == "googleSheets") {
          await this.renderGoogleSheet(event.fileTitle);
        }
      }
    },
    renderCsv(delimeter: string, stringMarker: string): string {
      const columnEntries = [
        ...Object.entries(this.computedGroupedColumns),
        ...Object.entries(this.visibleColumns),
      ].filter((col) => !col[1].rendererMetadata.actions);

      if (this.isAgGridActive) {
        columnEntries.sort(
          indexedKeyComparer(this.gridState.columnOrder, (x) => x[0])
        );
      }

      const columns = columnEntries.map((col) => col[1]); // Remove columns

      // escapes instances of `stringMarker` with {stringMarker}{stringMarker},
      // and then adds prefix and suffix.
      // i.e. `bob"bob` becomes `"bob""bob"` if stringMarker=`"`
      const stringFormatter = (s: string): string => {
        const matcher = RegExp("\\" + stringMarker, "g");
        let escaped = s.replace(matcher, stringMarker + stringMarker);
        return stringMarker + escaped + stringMarker;
      };

      const header = columns.map((col) => stringFormatter(col.headingText));

      const columnExtractors = columns.map(
        (col) => (item: any) =>
          col.csvformatter(col.extractor(item), item, stringFormatter)
      );
      const rowExtractor = (item: any) =>
        columnExtractors.map((a) => a(item)).join(delimeter);

      // Need flattend rows to handle grouping
      const contentRows = this.getFlattenedRows(this.virtualGrid).filter((r) =>
        isContentRow(r)
      ) as ContentRow[];

      let lines;

      if (this.isAgGridActive && this.$refs.agGridRef) {
        // Filter out grouped rows as they don't contain row data
        const agGridRows: RowNode[] = (
          (
            this.$refs.agGridRef as unknown as AgGridInterface
          )?.getSortedAndFilteredRows() ?? []
        ).filter((row) => !row.group);

        lines = [header].concat(
          agGridRows.map((row) => rowExtractor(row.data))
        );
      } else {
        lines = [header].concat(
          contentRows.map((row: ContentRow) => rowExtractor(row.item))
        );
      }
      return lines.join("\r\n");
    },
    mapGridToDocGenGridShape(title: string) {
      const visibleDataColumns = Object.entries(this.visibleColumns).filter(
        (c) => !c[1].rendererMetadata.actions
      );

      if (this.isAgGridActive) {
        visibleDataColumns.sort(
          indexedKeyComparer(this.gridState.columnOrder, (x) => x[0])
        );
      }

      const columnTitles = visibleDataColumns.map((c) => c[1].headingText);

      const mapVirtualGridToGenerateDocApi = (
        groupRow: GridGroupValue,
        rows: readonly Row[]
      ) => {
        // array of group rows
        if (rows[0] && isRowGroup(rows[0])) {
          groupRow.subGroups = rows.filter(isRowGroup).map((group) => {
            const rowGroup = new GridGroupValue();

            // First grouped column is special as it has to include the grouping name
            rowGroup.groupName = `${
              this.computedGroupedColumns[group.columnKey].headingText
            }: ${group.columnValue}`;

            rowGroup.cells = visibleDataColumns.slice(1).map((column) => {
              const data = group.aggregateValues[column[0]];
              return data && isNotNullOrUndefined(data.value)
                ? column[1].formatter(data.value, undefined).toString()
                : "";
            });

            mapVirtualGridToGenerateDocApi(rowGroup, group.children);
            return rowGroup;
          });
        }

        // array of content rows
        if (rows[0] && isContentRow(rows[0])) {
          groupRow.rows = rows.filter(isContentRow).map((row) => {
            const gridValue = new GridRowValue();
            gridValue.cells = visibleDataColumns.map(
              (columnDef) =>
                columnDef[1].extractAndFormat(row.item)?.toString() ?? ""
            );
            return gridValue;
          });
        }
      };

      const mapAgGridRowsToGenerateDocApi = (
        groupRow: GridGroupValue,
        rows: readonly RowNode[]
      ) => {
        if (rows[0] && rows[0].group) {
          groupRow.subGroups = rows
            .filter((row) => row.rowGroupIndex === rows[0].rowGroupIndex)
            .map((row) => {
              const rowGroup = new GridGroupValue();
              rowGroup.groupName = `${row.rowGroupColumn?.getUserProvidedColDef()?.headerName ?? ""}: ${row.key}`;

              rowGroup.cells = visibleDataColumns.slice(1).map((column) => {
                const value = row.aggData?.[column[0]];

                if (value === undefined) {
                  return "";
                }

                return (column[1].formatter(value, undefined) || "").toString();
              });

              mapAgGridRowsToGenerateDocApi(
                rowGroup,
                row.childrenAfterGroup ?? []
              );

              return rowGroup;
            });

          groupRow.rows = rows
            .filter((row) => !row.group)
            .map((row) => {
              const gridValue = new GridRowValue();
              gridValue.cells = visibleDataColumns.map((columnDef) =>
                (columnDef[1].extractAndFormat(row.data) || "").toString()
              );
              return gridValue;
            });
        } else {
          groupRow.rows = rows.map((row) => {
            const gridValue = new GridRowValue();
            gridValue.cells = visibleDataColumns.map((columnDef) =>
              (columnDef[1].extractAndFormat(row.data) || "").toString()
            );
            return gridValue;
          });
        }
      };

      const parentGroupRow = new GridGroupValue();
      if (this.isAgGridActive && this.$refs.agGridRef) {
        const agGridRows =
          (
            this.$refs.agGridRef as unknown as AgGridInterface
          )?.getSortedAndFilteredRows() ?? [];

        mapAgGridRowsToGenerateDocApi(
          parentGroupRow,
          agGridRows.some((row) => row.group)
            ? agGridRows.filter((row) => row.rowGroupIndex === 0)
            : agGridRows
        );
      } else {
        mapVirtualGridToGenerateDocApi(parentGroupRow, this.virtualGrid);
      }

      this.pdfTitle = title;
      this.pdfColumns = columnTitles;
      this.pdfGrid =
        parentGroupRow.subGroups.length > 0
          ? parentGroupRow.subGroups
          : [parentGroupRow];
    },
    async renderPdf(title: string): Promise<Uint8Array> {
      this.mapGridToDocGenGridShape(title);

      return await documentGenerationService.generateGridDocument(
        api.tenantId,
        EnumDocumentFileType.pdf,
        this.pdfTitle,
        this.pdfColumns,
        this.pdfGrid
      );
    },
    async generateGoogleSheet() {
      const url = await documentGenerationService.generateGoogleSheetFile(
        api.tenantId,
        this.pdfTitle,
        this.pdfColumns,
        this.pdfGrid
      );
      window.open(url, "_blank");
    },
    async renderGoogleSheet(title: string) {
      this.mapGridToDocGenGridShape(title);

      const isActive = await googleSheetLinkService.isGoogleSheetLinkActive(
        api.tenantId,
        userStore.state.user?.id ?? Guid.createEmpty()
      );

      if (!isActive) {
        const route = router.resolve(mainMenu.viewGoogleSheetSuccess());
        const callbackUrl = new URL(route.href, window.location.origin);

        const oauthUrl = await googleSheetLinkService.commenceGoogleSheetLink(
          api.tenantId,
          userStore.state.user?.id ?? Guid.createEmpty(),
          callbackUrl.href
        );

        const loginWindow = window.open(
          oauthUrl,
          messages.calendar_link_to_google,
          "width=500, height=650"
        );

        if (loginWindow) {
          loginWindow.opener.onSuccess = async () => {
            setTimeout(() => loginWindow.close(), 2000);
            await this.generateGoogleSheet();
          };
        }
      } else {
        await this.generateGoogleSheet();
      }
    },
    /**
     * The checkbox to select rows, groups, and everything (with header).
     */
    genSelectorControl(h: CreateElement, row?: Row): VNode {
      // Wrap the grid-selector in a label to increase the clickable area around the input
      return h(
        "label",
        {
          class: "bm-grid-selector-label no-print",
          on: {
            // Prevent the click from clicking the line, routing us away
            click: (e: Event) => e.stopPropagation(),
          },
        },
        [
          h(
            "input",
            {
              class: "bm-grid-selector",
              domProps: {
                type: "checkbox",
                checked: row
                  ? row.isSelected && this.rowSelectable(row)
                  : this.virtualGrid.length
                    ? this.virtualGrid.every(
                        (row) => row.isSelected || !this.rowSelectable(row)
                      )
                    : false,
                disabled: row
                  ? this.rowSelectable(row)
                    ? false
                    : true
                  : !this.virtualGrid.length,
              },
              on: {
                // Prevent the click event from bubbling to the row click handler
                click: (e: Event) => e.stopPropagation(),
                change: () => this.toggleSelectedRow(row),
              },
            },
            []
          ),
        ]
      );
    },
    /**
     * The arrow icon that shows and hides more rows/cards.
     */
    genExpanderControl(
      h: CreateElement,
      isExpanded: boolean,
      action: () => void,
      iconHidden: boolean = false
    ): VNode {
      return h(
        "VIcon",
        {
          staticClass: "expand-icon",
          class: {
            "expand-icon-active": isExpanded,
            "hidden-icon": iconHidden,
          },
          on: {
            click: (e: MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              action();
            },
          },
        },
        ["keyboard_arrow_down"]
      );
    },
    genDragHandle(h: CreateElement, row: ContentRow): VNode {
      // Function that we pass into the drag data object for drop clients to query for the selected item.
      const getSelectedItems = () => {};

      return h(
        Drag,
        {
          props: {
            data: this.draggableItems,
          },
          scopedSlots: {
            "drag-image": () => {
              return h(
                "v-badge",
                {
                  attrs: { color: "red" },
                  style: {
                    transform: "translate(10px, 5px)",
                  },
                  scopedSlots: {
                    badge: () => this.draggableItems.length.toString(),
                  },
                },
                []
              );
            },
          },
        },
        [
          h(
            "VIcon",
            {
              class: "drag-handle",
              on: {
                mousedown: () => {
                  // Keep track of the last "dragged element" so we can determine what row is being dragged in the
                  // raw drag handling code.
                  this.currentDraggedRow = row;
                },
                click: (e: Event) => e.stopPropagation(),
              },
            },
            ["drag_indicator"]
          ),
        ]
      );
    },
    genContentCell(
      h: CreateElement,
      column: BmGridColumnConfiguration,
      item: any,
      align: boolean = true
    ): VNode {
      const classes = column.classExtractor(item);
      const node = column.renderer(
        h,
        column.extractAndFormat(item),
        item,
        column
      );
      const isContentCell = !column.rendererMetadata.actions;
      return h(
        "div",
        {
          class: {
            "content-cell": isContentCell,
            "no-print": !isContentCell,
            "d-flex": align,
            "text-center justify-center": column.align === "center" && align,
            "text-right justify-end": column.align === "end" && align,
            [classes]: !!classes,
          },
        },
        [node]
      );
    },
    genTableViewGrid(h: CreateElement): VNode {
      return h("table", {}, [
        h("thead", this.genTableViewHeadRow(h)),
        h("tbody", this.genTableViewBodyRows(h)),
        h("tfoot", this.genTableViewFooter(h)),
      ]);
    },
    /**
     * Renders the <tr> heading row of the table.
     */
    genTableViewHeadRow(h: CreateElement): VNodeChildren {
      const tableHeadCells = Object.entries(this.visibleColumns).map(
        ([key, value], index) => {
          const style = value.shrink ? { width: "1%", minWidth: "1%" } : {};

          const sortIndex = this.gridState.sortBy.findIndex(
            (sortBy) => sortBy.columnKey === key
          );

          // We deal with 3 elements in the column header div.
          //  Value
          //  Sort icon (optional)
          //  Sort count (optional)
          // For End-Aligned colums we want the icon and count before value : Icon-Count-Value
          // For other alignments we want the icon and count after value : Value-Icon-Count
          let headerElements: VNode[] = [];

          // Dont display sort arrow if the column is not sortable
          if (
            (this.allowSort && value.sortableAndFilterable) ||
            sortIndex !== -1 // Or if it's sorted already
          ) {
            headerElements.push(
              h(
                "VIcon",
                {
                  staticClass: "expand-icon",
                  class: {
                    "expand-icon-active": !(
                      this.gridState.sortBy[sortIndex]?.descending ?? false
                    ),
                    "hover-reveal-icon": sortIndex === -1,
                  },
                },
                ["keyboard_arrow_down"]
              )
            );
          }

          if (this.gridState.sortBy.length > 1 && sortIndex >= 0) {
            headerElements.push(h("span", (sortIndex + 1).toString()));
          }

          if (value.align === "end") {
            // Value goes AFTER Icon-Count
            headerElements.push(h("span", value.headingText));
          } else {
            // Value goes BEFORE Icon-Count
            headerElements.unshift(h("span", value.headingText));
          }

          const headingContents = h(
            "div",
            {
              staticClass: "d-flex align-center header-cell",
              class: {
                "justify-center": value.align === "center",
                "justify-end": value.align === "end",
              },
            },
            headerElements
          );

          return h(
            "th",
            {
              class: {
                "no-print": this.isActionsColumn(value),
              },
              key,
              attrs: {
                "data-qa": key + "_heading",
              },
              on: {
                click: (e: MouseEvent) => {
                  if (this.allowSort && value.sortableAndFilterable) {
                    this.gridState.applyColumnSort(key, e.shiftKey);
                  }
                },
              },
              style,
            },
            index === 0
              ? this.genTableViewFirstColumnControls(h, [headingContents])
              : [headingContents]
          );
        }
      );

      return [
        h(
          this.allowDrop ? Drop : "tr",
          {
            attrs: { tag: "tr" },
            on: { drop: (e: DnDEvent) => this.onRowDrop(null, e) },
          },
          tableHeadCells
        ),
      ];
    },
    onRowDrop(row: Row | null, event: DnDEvent) {
      let keyAbove = undefined;
      if (row) {
        keyAbove = row.rowKey;
      }
      this.$emit("drag-drop", {
        draggedRowKeys: event.data,
        droppedOnRowKey: keyAbove,
      } as GridDragDropEvent);
    },
    onDragOver(row: Row | null) {
      // get element being dragged over
      if (row) {
        row.dropBelow = true;
      }
    },
    genTableViewFirstColumnControls(
      h: CreateElement,
      content: VNodeChildren,
      row?: Row,
      depth: number = 0
    ): VNodeChildren {
      const spacerWidthPixels = 12;
      let controls = [];
      let spacer = spacerWidthPixels * depth;
      const gridHasExpanders = this.allowExpand || this.hasGrouping;

      // add drag handle on content rows
      if (this.allowDrag && isContentRow(row) && this.rowDraggable(row)) {
        controls.push(this.genDragHandle(h, row));
      } else if (
        this.allowDrag &&
        (!isContentRow(row) || !this.rowDraggable(row))
      ) {
        // Add some spacing to the header row to align header above drag icon.
        spacer += 24;
      }

      // add spacing to the header if we know expanders exist in the grid
      // or it's a content row, in an expandable grid, that is not expandable
      // or it's a content row, in a non-expandable grid, that has group expanders
      if (
        (row === undefined && gridHasExpanders) ||
        (isContentRow(row) && this.allowExpand && !this.rowExpandable(row)) ||
        (isContentRow(row) && !this.allowExpand && gridHasExpanders)
      ) {
        spacer += spacerWidthPixels * 2;
        // Add an expander if it's a group row or it's a content row is an expandable grid
      } else if (isRowGroup(row) || (isContentRow(row) && this.allowExpand)) {
        controls.push(
          this.genExpanderControl(h, row.isExpanded, () => {
            row.isExpanded = !row.isExpanded;
            this.refreshVirtualGrid();
            this.$emit("expand-row", row);

            if (this.accordian) {
              this.$emit(
                "update:expanded-item-keys",
                row.isExpanded ? [row.rowKey] : []
              );
            }
          })
        );
      }

      if (this.allowSelect) {
        controls.push(this.genSelectorControl(h, row));
      }

      controls.push(content);

      return [
        h(
          "div",
          {
            class: "bm-grid-row-controls",
            style: {
              "margin-left": spacer + "px",
            },
          },
          controls
        ),
      ];
    },
    genTableViewBodyRows(h: CreateElement): VNodeChildren {
      if (this.virtualGrid.length == 0) {
        return this.genTableViewNoData(h);
      } else {
        if (!this.hasGrouping) {
          return this.genTableViewContentRows(
            h,
            this.virtualGrid.filter(
              (r) => r.rowType === "content"
            ) as ContentRow[]
          );
        } else {
          return this.genTableViewGroupRows(
            h,
            this.virtualGrid.filter((r) => r.rowType === "group") as RowGroup[],
            0
          );
        }
      }
    },
    genTableViewGroupRows(
      h: CreateElement,
      group: RowGroup[],
      depth: number = 0
    ): VNodeChildren {
      return group.map((group) => {
        // Turn the aggregate values for this row group into cells (we have to ignore the first cell as it sits under
        // grouping identifier. This might not be ideal in some situations).
        const aggregateCells = Object.keys(this.visibleColumns)
          .slice(1)
          .map((columnKey) => {
            const aggregateData = group.aggregateValues[columnKey];

            // Determine column alignment from placement column
            let alignment: ColumnAlignment = "start";
            if (aggregateData) {
              alignment =
                aggregateData.summary.placement === SummaryPlacement.Column
                  ? this.visibleColumns[columnKey].align
                  : aggregateData.summary.align;
            }

            const groupCell =
              aggregateData &&
              h(
                "div",
                {
                  class: {
                    "content-cell": true, // cannot be an action column
                    "d-flex": true,
                    "text-center justify-center": alignment === "center",
                    "text-right justify-end": alignment === "end",
                  },
                },
                [
                  aggregateData.summary.renderer(
                    h,
                    this.visibleColumns[columnKey].formatter(
                      aggregateData.value,
                      undefined
                    ),
                    undefined,
                    aggregateData.summary
                  ),
                ]
              );

            return h("td", {}, [groupCell]);
          });

        return [
          h("tr", { class: `group group-index-${depth}` }, [
            h("td", [
              this.genTableViewFirstColumnControls(
                h,
                `${this.computedGroupedColumns[group.columnKey].headingText}: ${
                  group.columnValue
                }`,
                group,
                depth
              ),
            ]),
            aggregateCells,
          ]),

          group.isExpanded
            ? group.isParentOfGroups
              ? this.genTableViewGroupRows(
                  h,
                  group.children as RowGroup[],
                  depth + 1
                )
              : this.genTableViewContentRows(
                  h,
                  group.children as ContentRow[],
                  depth + 1
                )
            : [],
        ];
      });
    },
    genShowAllRowsControl(
      isTableView: boolean,
      h: CreateElement,
      remainingRows: number
    ): VNodeChildren {
      const linkText = translateWithParams("show_all_rows", {
        remainingRows: remainingRows.toString(),
      });

      const onClick = {
        click: () => (this.showAllRows = true),
      };

      const showAllLinkElement = h(
        "a",
        {
          class: "bm-link",
        },
        linkText
      );

      if (isTableView) {
        return [
          h("tr", { class: "grid-show-all" }, [
            h(
              "td",
              {
                class: "text-center",
                attrs: { colspan: Object.keys(this.visibleColumns).length },
                on: onClick,
              },
              [showAllLinkElement]
            ),
          ]),
        ];
      } else {
        return [
          h(
            "div",
            {
              class: "grid-show-all text-center",
              on: onClick,
            },
            [showAllLinkElement]
          ),
        ];
      }
    },
    /**
     * Generates the content rows - with potential expander rows as siblings.
     */
    genTableViewContentRows(
      h: CreateElement,
      rows: ContentRow[],
      depth: number = 0
    ): VNodeChildren {
      let lastRow: VNodeChildren = [];

      // Apply row limit if required
      if (this.visibleRowLimit && rows.length > this.visibleRowLimit) {
        lastRow = [
          this.genShowAllRowsControl(
            true,
            h,
            rows.length - this.visibleRowLimit
          ),
        ];
        rows = rows.slice(0, this.visibleRowLimit);
      }

      return [
        rows.map((row, index) => [
          this.genTableViewContentRow(h, row, index, depth),
          this.genTableViewExpandableRow(h, row, index),
        ]),
        lastRow,
      ];
    },
    isActionsColumn(columnDefinition: BmGridColumnConfiguration) {
      return !!columnDefinition.rendererMetadata.actions;
    },
    isCellClickable(index: number): boolean {
      return index !== 0 ? true : this.shouldAllowRowClickRouteOnSelectableCell;
    },
    genTableViewContentRow(
      h: CreateElement,
      row: ContentRow,
      rowIndex: number,
      depth: number
    ): VNodeChildren {
      let rowChildElements: VNodeChildren = [];

      rowChildElements = Object.values(this.visibleColumns).map(
        (columnDefinition, index) => {
          let cellFromRow = [
            this.genContentCell(h, columnDefinition, row.item),
          ];

          let contentCell =
            index === 0
              ? this.genTableViewFirstColumnControls(h, cellFromRow, row, depth)
              : cellFromRow;

          // If we have a route function
          let routeLocation = this.rowClickRoute
            ? this.rowClickRoute(row.item)
            : null;

          // Wraps the cell if clickable, using <router-link> if rowClickRoute is provided
          let wrappedContentCell =
            this.isCellClickable(index) &&
            this.isRowClickable &&
            !this.isActionsColumn(columnDefinition)
              ? h(
                  routeLocation ? "router-link" : "a", // Use default <a>'s for @row-clickable
                  {
                    props: {
                      to: routeLocation,
                    },
                  },
                  contentCell
                )
              : contentCell;

          return [
            h(
              "td",
              { class: { "no-print": this.isActionsColumn(columnDefinition) } },
              [wrappedContentCell]
            ),
          ];
        }
      );

      // Calculate extra row classes
      let rowClasses: { [key: string]: boolean } = {
        content: true,
        selected: row.isSelected,
        even: rowIndex % 2 === 0,
      };

      this.rowClasses(row).forEach((newClass) => (rowClasses[newClass] = true));

      // wrap everything in a table row
      return [
        h(
          this.allowDrop ? Drop : "tr",
          {
            attrs: {
              tag: "tr",
            },
            key: row.rowKey,
            on: {
              click: () => {
                if (row && this.isRowClickable) {
                  this.onRowClick(row);
                }
              },
              drop: (e: DnDEvent) => this.onRowDrop(row, e),
              dragover: (e: DnDEvent) => this.onDragOver(row),
            },
            class: [
              rowClasses,
              { "row-clickable": this.isRowClickable },
              { "drop-below": row.dropBelow },
            ],
          },
          rowChildElements
        ),
      ];
    },
    genTableViewExpandableRow(
      h: CreateElement,
      row: ContentRow,
      rowIndex: number
    ): VNodeChildren {
      if (
        this.allowExpand &&
        this.$scopedSlots["expand"] !== undefined &&
        row.isExpanded &&
        this.rowExpandable(row)
      ) {
        return [
          h(
            "tr",
            {
              class: {
                expand: true,
                even: rowIndex % 2 === 0,
              },
            },
            [
              h(
                "td",
                {
                  class: "animateIn",
                  attrs: { colspan: Object.keys(this.visibleColumns).length },
                },
                [this.$scopedSlots["expand"]!(row)]
              ),
            ]
          ),
        ];
      }

      return [];
    },
    genTableViewFooter(h: CreateElement): VNodeChildren {
      let columnDefinitions = Object.values(this.visibleColumns);

      if (columnDefinitions.some((def) => def.footer !== null)) {
        let footerCols = [];
        for (const columnDefinition of columnDefinitions) {
          footerCols.push(
            h("td", {}, [
              columnDefinition.footer !== null
                ? columnDefinition.footer(h)
                : "",
            ])
          );
        }
        return [h("tr", {}, footerCols)];
      }

      return [];
    },
    genTableViewNoData(h: CreateElement): VNodeChildren {
      return [
        h("tr", {}, [
          h(
            "td",
            {
              class: "grid-no-data",
              attrs: {
                colspan: Object.keys(this.visibleColumns).length.toString(),
              },
            },
            this.$scopedSlots["no-data"]
              ? this.$scopedSlots["no-data"]!({})
              : [h("span", {}, this.$m.grid_no_data)]
          ),
        ]),
      ];
    },
    genCardViewGrid(h: CreateElement): VNodeChildren {
      if (this.virtualGrid.length === 0) {
        return this.genCardViewNoData(h);
      } else {
        return this.genCardViewGridCards(h, this.virtualGrid);
      }
    },
    genCardViewGridCards(
      h: CreateElement,
      rows: readonly Row[],
      depth: number = 0
    ): VNodeChildren {
      let nodes: VNodeChildren = [];

      if (this.virtualGrid.length == 0) {
        return this.genTableViewNoData(h);
      }

      for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
        const row = rows[rowIndex];

        if (this.visibleRowLimit && this.visibleRowLimit === rowIndex) {
          nodes.push(
            this.genShowAllRowsControl(false, h, rows.length - rowIndex)
          );
          break;
        }

        if (row.rowType === "group") {
          nodes.push(this.genCardViewGroup(h, row as RowGroup, depth));
        }

        if (row.rowType === "content") {
          nodes.push(
            this.genCardViewContent(h, row as ContentRow, depth, rowIndex)
          );
        }
      }

      return nodes;
    },
    genCardViewGroup(
      h: CreateElement,
      row: RowGroup,
      depth: number
    ): VNodeChildren {
      let nodes = [];
      let groupClasses: { [p: string]: boolean } = {
        card: true,
        group: true,
      };
      groupClasses[`group-index-${depth}`] = true;

      const groupExpander = this.genExpanderControl(h, row.isExpanded, () => {
        row.isExpanded = !row.isExpanded;
        this.refreshVirtualGrid();
      });

      // Turn the aggregate values for this row group into cells (we have to ignore the first cell as it sits under
      // grouping identifier. This might not be ideal in some situations).
      const aggregateCells = Object.keys(this.visibleColumns)
        .map((columnKey) => row.aggregateValues[columnKey])
        .flatMap((aggregateData) => {
          if (!aggregateData || aggregateData.summary.columnKey == undefined) {
            return [];
          }

          let column = this.visibleColumns[aggregateData.summary.columnKey];
          let cell = aggregateData.summary.renderer(
            h,
            column.formatter(aggregateData.value, undefined),
            undefined,
            aggregateData.summary
          );

          return [
            h("div", { class: "pa-2" }, [
              h("div", { class: "heading" }, column.headingText),
              h("div", [cell]),
            ]),
          ];
        });

      nodes.push(
        this.genCardViewCard(
          h,
          groupClasses,
          `${this.computedGroupedColumns[row.columnKey].headingText}: ${
            row.columnValue
          }`,
          aggregateCells,
          undefined,
          [groupExpander],
          undefined,
          depth,
          row
        )
      );

      if (row.isExpanded) {
        nodes.push(this.genCardViewGridCards(h, row.children, depth + 1));
      }

      return nodes;
    },
    genCardViewContent(
      h: CreateElement,
      content: ContentRow,
      depth: number,
      rowIndex: number
    ): VNodeChildren {
      let nodes = [];
      let expander = undefined;

      // Determine any special columns that need presenting in card view in a non-standard way.
      const actionColumnKey = Object.entries(this.visibleColumns).find(
        ([_, column]) =>
          column.rendererMetadata && column.rendererMetadata.actions
      )?.[0];
      const titleColumnKey = Object.keys(this.visibleColumns)?.[0];
      const fieldColumnKeys = Object.keys(this.visibleColumns).filter(
        (k) => k !== actionColumnKey && k !== titleColumnKey
      );

      const isExpandable =
        this.allowExpand &&
        this.$scopedSlots["expand"] &&
        this.rowExpandable(content);

      // Render the expandable content if required
      const expandableSection = isExpandable
        ? [
            h("div", { class: "" }, [
              content.isExpanded
                ? [
                    h(
                      "div",
                      {
                        class: "expand animateIn",
                      },
                      [[this.$scopedSlots["expand"]!(content)]]
                    ),
                  ]
                : undefined,
              h(
                "div",
                { class: "card-expand-controls d-flex justify-center" },
                [
                  h(
                    BmBtnExpand,
                    {
                      props: {
                        expanded: content.isExpanded,
                      },
                      on: {
                        change: (value: boolean) => {
                          content.isExpanded = value;
                          this.refreshVirtualGrid();
                          this.$emit("expand-row", content);

                          if (this.accordian) {
                            this.$emit("update:expanded-item-keys", [
                              content.rowKey,
                            ]);
                          }
                        },
                      },
                    },
                    []
                  ),
                ]
              ),
            ]),
          ]
        : undefined;

      const fields = fieldColumnKeys.map((columnKey) => {
        const column = this.visibleColumns[columnKey];
        const twelvePointGridColumnWidth =
          this.getTwelvePointColumnValueForColumn(columnKey, column);

        return h(
          "div",
          {
            // It's much nicer for all things to be left-aligned in card view
            class: `pa-2 flex xs${twelvePointGridColumnWidth} text-left justify-start`,
          },
          [
            h("div", { class: "heading" }, column.headingText),
            this.genContentCell(h, column, content.item, false),
          ]
        );
      });

      const actionField =
        actionColumnKey &&
        this.genContentCell(
          h,
          this.visibleColumns[actionColumnKey],
          content.item,
          false
        );
      const titleField =
        titleColumnKey &&
        this.genContentCell(
          h,
          this.visibleColumns[titleColumnKey],
          content.item,
          false
        );

      // Calculate extra row classes
      let rowClasses: { [key: string]: boolean } = {
        content: true,
        card: true,
        even: rowIndex % 2 === 0,
      };

      this.rowClasses(content).forEach(
        (newClass) => (rowClasses[newClass] = true)
      );

      nodes.push(
        this.genCardViewCard(
          h,
          rowClasses,
          [titleField],
          fields,
          [actionField],
          undefined,
          [expandableSection],
          depth,
          content
        )
      );

      return nodes;
    },
    genCardViewCard(
      h: CreateElement,
      classes: { [key: string]: boolean },
      title: VNodeChildren,
      fields: VNode[],
      actions?: VNodeChildren,
      groupExpander?: VNodeChildren,
      expandedContent?: VNodeChildren,
      depth: number = 0,
      row?: Row
    ): VNode {
      let titleElements = [];

      // Expander
      if (groupExpander !== undefined) {
        titleElements.push(groupExpander);
      }

      if (this.allowSelect) {
        titleElements.push(this.genSelectorControl(h, row));
      }

      // Title
      titleElements.push(h("div", { class: "card-title" }, title));

      // Actions
      if (actions !== undefined) {
        titleElements.push(h("div", { class: "card-actions" }, actions));
      }

      let cardStack = [
        h(
          "div",
          {
            class: "d-flex pa-2 card-top align-center",
            on: {
              click: () => {
                if (isContentRow(row) && this.isRowClickable) {
                  this.onRowClick(row);
                }
              },
            },
          },
          titleElements
        ),
        fields?.length > 0
          ? h("div", { class: "d-flex flex-wrap card-fields" }, fields)
          : undefined,
        expandedContent !== null
          ? h("div", { class: "" }, expandedContent)
          : [],
      ];

      // Add depth class
      classes[`depth-${depth}`] = true;

      return h("div", { class: classes }, cardStack);
    },
    genCardViewNoData(h: CreateElement): VNodeChildren {
      return [
        h(
          "div",
          {
            class: "card-no-data text-center",
            attrs: {},
          },
          this.$scopedSlots["no-data"]
            ? this.$scopedSlots["no-data"]!({})
            : [h("span", {}, this.$m.grid_no_data)]
        ),
      ];
    },
    genSummarySlot(h: CreateElement): VNodeChildren {
      return [
        h(
          "div",
          {
            class: "grid-summary text-right",
            attrs: {},
          },
          this.$scopedSlots["summary"]!({})
        ),
      ];
    },
    /**
     * Calculates the correct 12 point grid width for a card item based on the grid and column configuration.
     */
    getTwelvePointColumnValueForColumn(
      columnKey: string,
      column: BmGridColumnConfiguration
    ) {
      if (
        column.cardColumnSpan < 1 ||
        column.cardColumnSpan > this.cardColumns
      ) {
        throw new BrewManError(
          `Invalid value for cardColumnSpan '${column.cardColumnSpan}' for column '${columnKey}'`
        );
      }

      const twelvePointGridColumnWidth = Math.floor(
        (12 / this.cardColumns) * column.cardColumnSpan
      );

      if (twelvePointGridColumnWidth < 1 || twelvePointGridColumnWidth > 12) {
        throw new BrewManError(
          `Invalid calculated value for grid column value '${twelvePointGridColumnWidth}' for column '${columnKey}'`
        );
      }

      return twelvePointGridColumnWidth;
    },
    onRowClick(row: ContentRow) {
      this.$emit("row-click", row.item);
    },
    getFilteredItems() {
      if (this.isAgGridActive && this.$refs.agGridRef) {
        const agGridRows =
          (
            this.$refs.agGridRef as unknown as AgGridInterface
          )?.getSortedAndFilteredRows() ?? [];
        return agGridRows.map((row) => row.data);
      } else {
        const contentRows = this.getFlattenedRows(this.virtualGrid).filter(
          (r) => isContentRow(r)
        ) as ContentRow[];
        return contentRows.map((row: ContentRow) => row.item);
      }
    },
  },
  render(h: CreateElement): VNode {
    if (this.isAgGridActive) {
      return h("div", {}, [
        h(AgGrid, {
          props: {
            ...this.$props,
            height: this.gridHeightWithUnits,
            expandSlotRenderFn: this.$scopedSlots["expand"],
            itemKey: this.gridConfiguration.itemKey ?? this.itemKey,
          },
          ref: "agGridRef",
          on: {
            "filter-summary": (event: Event) =>
              this.$emit("filter-summary", event),
            "drag-drop": (event: GridDragDropEvent) =>
              this.$emit("drag-drop", event),
            // Event should be the row-item here
            "row-click": (event: any) => this.$emit("row-click", event),
            "update:selected-item-keys": (event: any[]) =>
              this.$emit("update:selected-item-keys", event),
            "expand-row": (event: any) => this.$emit("expand-row", event),
          },
        }),
        this.$scopedSlots["summary"] ? this.genSummarySlot(h) : "",
      ]);
    }
    return h(
      "div",
      {
        style: {
          "max-height": this.isCardView ? "100vh" : this.gridHeightWithUnits,
          "overflow-y": "auto", // To prevent overflow in mobile view
        },
        class: {
          "bm-grid": true,
          "bm-grid-card-view": this.isCardView,
          "bm-grid-table-view": !this.isCardView,
          grouped: this.hasGrouping,
          "sub-grid": this.subGrid,
        },
      },
      [
        this.isCardView ? this.genCardViewGrid(h) : this.genTableViewGrid(h),
        this.$scopedSlots["summary"] ? this.genSummarySlot(h) : "",
      ]
    );
  },
});
</script>

<style scoped lang="sass">
@import "src/sass/typography"
@import "src/sass/colours"

@mixin heading-text
  @include bm-body
  color: $bm-color-text01

@mixin contents-text
  @include bm-body2
  color: $bm-color-text01


// Remove a-tag styling for the grid rows
.row-clickable a
  text-decoration: none
  color: $bm-color-text01

// Table view styles
.bm-grid-table-view
  overflow-x: auto

.bm-grid-table-view table
  width: 100%
  border-collapse: separate
  border-spacing: 0


// Pad out content cells to match parents and give spacing
.content-cell
  padding: 8px
  overflow: hidden
  text-overflow: ellipsis

.bm-grid-table-view th
  @include heading-text
  text-align: left
  border-bottom: 3px solid #D1CFD7
  padding-bottom: 3px
  vertical-align: bottom
  position: sticky
  position: -webkit-sticky
  top: 0
  background-color: $bm-color-background01
  z-index: 2


.sub-grid th
  z-index: 1

.bm-grid-table-view td
  @include contents-text
  border-bottom: 1px solid #D1CFD7

.bm-grid-table-view tr
  height: 36px

// Row/Card colours
.bm-grid .content.even
  background-color: $bm-color-background01

.bm-grid .content:not(.even)
  background-color: $bm-color-background04

.bm-grid .group.group-index-0 > td
  color: $bm-color-text04

.bm-grid .group.group-index-0, .bm-grid .group.group-index-0 .v-icon
  background-color: #73777A
  color: $bm-color-text04

.bm-grid .group.group-index-1 > td
  color: $bm-color-text04

.bm-grid .group.group-index-1, .bm-grid .group.group-index-1 .v-icon
  background-color: #989898
  color: white

// expander arrow inherit row/card text colour
.bm-grid i.expand-icon
  color: inherit

.bm-grid .expand
  background-color: transparent

.bm-grid-table-view .group
  @include bm-field-label
  padding-left: 0

// position cell block content next to the expander icon
.expandable-cell
  display: flex

.bm-grid-table-view .hidden-icon
  visibility: hidden

// Tidier to remove expander in card view
.bm-grid-card-view .hidden-icon
  display: none

.grid-no-data
  text-align: center

// These rules make the subgrid transparent and deep margin'd
// Use margin not padding so that content isn't visible behind sticky header scrolling
.bm-grid-table-view .sub-grid
  margin: 20px 30px

div.sub-grid table tr, div.sub-grid table thead tr
  background: transparent !important

.grid-summary
  background: $bm-color-grid-summary
  margin-top: 16px
  padding: 16px

.grid-summary ul
  list-style: none

// Animating the grid on expand

@keyframes slideIn
  0%
    opacity: 0
    transform: translateY(-20px)

  100%
    opacity: 1
    transform: translateY(0)

:deep(.animateIn)
  overflow: hidden
  animation: slideIn .3s ease-out
  transition: height .3s ease-out

:deep(.animateIn tbody)
  overflow: hidden
  animation: slideIn .4s ease-out

// Mobile grid

.bm-grid-card-view .card-top
  height: 100%
  border-bottom: 1px solid $bm-color-borders

.bm-grid-card-view .card-top .card-title
  flex-grow: 1 !important
  overflow-y: hidden

.bm-grid-card-view td
  border: 1px solid $bm-color-borders
  border-radius: 5px
  display: block
  min-height: 36px

.bm-grid-card-view .card
  @include contents-text
  border: 1px solid $bm-color-borders
  border-radius: 5px
  margin-bottom: 10px

.bm-grid-card-view .card .heading
  @include heading-text

// revert heading text color when a group card
.bm-grid-card-view .card.group .heading
  color: inherit

// indentation of cards
.bm-grid-card-view .card.depth-1
  margin-left: 10px

.bm-grid-card-view .card.depth-2
  margin-left: 20px

// put some padding on the expand content and remove the text centering
.bm-grid-card-view .expand
  margin: 10px auto
  text-align: initial

.bm-grid-card-view .card-expand-controls
  border-top: 1px solid $bm-color-borders

.bm-grid-card-view .sub-grid
  padding: 5px 15px

// Useful row classes clients can use

table tbody tr.grid-row-error, .bm-grid-card-view div.card.grid-row-error
  background-color: $bm-color-incomplete-row !important

table tbody tr.grid-row-warn, .bm-grid-card-view div.card.grid-row-warn
  background-color: $bm-color-warn-row !important

table tbody tr.grid-row-success, .bm-grid-card-view div.card.grid-row-success
  background-color: $bm-color-successful-row !important

.bm-grid :deep(.bm-grid-clickable)
  text-decoration: underline
  text-underline-position: under
  text-decoration-style: dotted
  text-decoration-color: darkgrey
  cursor: pointer
  padding-bottom: 1px
// Ensure the underline is always visible

.bm-grid :deep(p)
  width: 100%
  margin: 0

.bm-grid-row-controls
  display: flex
  align-items: center

thead .bm-grid-row-controls
  align-items: flex-end

// Increase the area the input box can be selected by
.bm-grid-selector-label
  padding: 15px

// Remove the bonus clickable area padding for heading row
thead .bm-grid-selector-label
  padding-bottom: 0

.row-clickable td
  cursor: pointer
  transition: background .1s ease-in-out

.row-clickable:hover td
  background: $bm-color-text03

// The copy of the dragged item at the final location
.sortable-ghost
  opacity: 0.5

// Hide the dragging ghost image as it doesn't make much sense - especially when multi dragging
.sortable-drag
  opacity: 0

.drag-handle
  cursor: move

// A line under a dragged over row to show where items will end up
.drop-in td, .drop-in th
  border-bottom: 3px solid
  border-top: inherit
  transition: 0.1s

.drop-in.drop-above td
  border-top: 3px solid
  border-bottom: inherit
  transition: 0.1s

.grid-show-all
  height: 40px !important

.grid-show-all td
  border-bottom-style: none


.expand-icon
  user-select: none
  cursor: pointer

.expand-icon-active
  transform: rotate(-180deg)

// Sorting effects for grid header
th:first-child
  padding-left: 0

.header-cell
  padding: 0 8px

th
  cursor: pointer
  user-select: none
  white-space: nowrap

.header-cell .hover-reveal-icon
  opacity: 0

.header-cell:hover .hover-reveal-icon
  opacity: 0.3

@media print
  /* Expand all information in a BM-Grid */
  .bm-grid
    max-height: 100% !important
    overflow: visible !important

  .expand-icon
    display: none
</style>
